// SPDX-FileCopyrightText: © 2023 Schneider Electric
//
// SPDX-License-Identifier: LGPL-2.1-only

import type { Importance, Size, Tag } from "@quartzds/core";
import type { ComponentInterface } from "@stencil/core";
import { Component, h, Host, Listen, Prop, State, Watch } from "@stencil/core";

import {
  cleanText,
  convertToBold,
  convertToGreen,
  escapeHtml,
  parseValue,
} from "../utils";

export type Position = "end" | "start";

/**
 * @slot Content to display for the block promo's CTA slot.
 * @see https://quartz.se.com/build/components/block-promo
 */
@Component({
  tag: "qds-web-block-promo",
  shadow: true,
  styleUrl: "block-promo.css",
})
export class BlockPromo implements ComponentInterface {
  /**
   * Text to display for the title.
   */
  @Prop() public readonly mainTitle: string;

  /**
   * HTML tag to use to render the title.
   */
  @Prop() public readonly titleTag?: Extract<Tag, "h2" | "h3" | "h4"> = "h2";

  /**
   * Text to display for the kicker.
   */
  @Prop() public readonly kicker?: string;

  /**
   * Text to display for the subtitle.
   */
  @Prop() public readonly subtitle?: string;

  /**
   * Text to display for the description.
   */
  @Prop() public readonly description?: string;

  /**
   * Text to display in an unordered list. As HTML attributes can only be strings, the values array must be stringified by the consumer into a string to be internally parsed into a string[].
   */
  @Prop() public readonly descriptionList?: string[] | string;

  /**
   * The block's promo importance.
   */
  @Prop() public readonly importance?: Importance = "standard";

  /**
   * The block's promo size.
   */
  @Prop() public readonly size?: Size = "standard";

  /**
   * Alt text for the image to display.
   */
  @Prop() public readonly imageAlt?: string;

  /**
   * Allows to specify the layout width of the image for each of a list of media conditions. The value should be a string containing a comma-separated list of source size descriptors followed by an optional fallback size.
   * @remark Combined with a srcSet content in the imageSrc property, this provides the ability to automatically select among different images—even images of different orientations or aspect ratios—as the document state changes to match different media conditions.
   * @see https://developer.mozilla.org/en-US/docs/Web/API/HTMLImageElement/sizes
   *
   * @public
   * @readonly
   * @type {string}
   *
   */
  @Prop() public readonly imageSizes: string;

  /**
   * Src of the image to display.
   *
   * @remark To specify the source path or url based on the responsive image srcSet attribute add one or more strings separated by commas, indicating possible image sources for the user agent to use.
   * @see https://developer.mozilla.org/en-US/docs/Web/HTML/Element/img#srcset
   *
   * @public
   * @readonly
   * @type {string}
   *
   */
  @Prop() public readonly imageSrc?: string;

  /**
   * Position to display the image.
   */
  @Prop() public readonly imagePosition?: Position = "end";

  /**
   * Src of the video to display.
   */
  @Prop() public readonly videoSrc?: string;

  /**
   * Parsed values to display in the descriptionList property.
   */
  @State() private parsedDescriptionList?: string[];

  @State() private hideTagsSlot: boolean;

  @State() private hideGroupSlot: boolean;

  @State() private isMobile: boolean = window.innerWidth <= 1023;

  /**
   * Whenever `descriptionList` changes this method will check if it is an string[].
   * If it's not it will convert it.
   * @param newValue
   */
  @Watch("descriptionList")
  private descriptionListChanged(newValue: string[] | string): void {
    this.parsedDescriptionList =
      typeof newValue === "string" ? parseValue(newValue) : newValue;
  }

  @Listen("resize", { target: "window" })
  private handleResize(): void {
    this.isMobile = window.innerWidth <= 1023;
  }

  public componentWillLoad(): void {
    if (this.descriptionList !== undefined)
      this.descriptionListChanged(this.descriptionList);
  }

  public render() {
    const computedAlignment = this.getComputedAlignment();

    return (
      <Host
        class={{
          "qds-theme-dark":
            this.importance === "emphasized" &&
            (this.size !== "small" || this.isMobile),
        }}
      >
        <section
          aria-label={cleanText(this.mainTitle)}
          class="flex"
          data-importance={this.importance}
          data-size={this.size}
        >
          <div
            class={{
              "content-area": true,
              flex: true,
              ...(this.imagePosition && {
                [this.imagePosition]:
                  this.importance === "standard" ||
                  this.importance === "emphasized",
              }),
            }}
          >
            {this.importance !== "subdued" && (
              <qds-web-media
                class={{
                  image: this.importance === "emphasized",
                }}
                label-name={cleanText(this.mainTitle)}
                loading="lazy"
                loop
                src-video={
                  this.importance === "standard" ? this.videoSrc : undefined
                }
                image-src={this.imageSrc}
                image-alt={this.imageAlt}
                image-sizes={this.imageSizes}
                buttonSize="hero"
              />
            )}
            <div class="body flex">
              {this.importance === "standard" && this.size !== "small" && (
                <div class="tags flex" hidden={this.hideTagsSlot}>
                  <slot name="tags" onSlotchange={this.handleSlotChange} />
                </div>
              )}
              <div class="content flex">
                <qds-title
                  class="qds-title flex"
                  subtitle={this.subtitle}
                  kicker={this.kicker}
                  level="section"
                  layer="main"
                  alignment={computedAlignment}
                  tag={this.titleTag}
                  variant="web-main-section-hero"
                  innerHTML={convertToGreen(escapeHtml(this.mainTitle))}
                />
                {(this.description ?? "") && (
                  <p
                    class="description"
                    innerHTML={convertToBold(
                      escapeHtml(this.description ?? ""),
                    )}
                  />
                )}
                {this.parsedDescriptionList && (
                  <ul class="description-list">
                    {this.parsedDescriptionList.map((item) => (
                      <li
                        key={item}
                        innerHTML={convertToBold(escapeHtml(item))}
                      />
                    ))}
                  </ul>
                )}
              </div>
              <div class="group flex" hidden={this.hideGroupSlot}>
                <slot onSlotchange={this.handleSlotChange} />
              </div>
            </div>
          </div>
        </section>
      </Host>
    );
  }

  private getComputedAlignment(): string {
    const isEmphasizedNotSmall =
      this.importance === "emphasized" && this.size !== "small";
    const isEmphasizedMobile =
      this.importance === "emphasized" && this.isMobile;
    const isSubduedNotSmall =
      this.importance === "subdued" && this.size !== "small";
    const isSubduedMobile = this.importance === "subdued" && this.isMobile;

    return isEmphasizedNotSmall ||
      isSubduedNotSmall ||
      isEmphasizedMobile ||
      isSubduedMobile
      ? "center"
      : "start";
  }

  private readonly handleSlotChange = (event: Event): void => {
    const slot = event.target as HTMLSlotElement;
    if (slot.name === "tags") {
      this.hideTagsSlot = slot.assignedElements().length === 0;
    } else {
      this.hideGroupSlot = slot.assignedElements().length === 0;
    }
  };
}
